import React, { useCallback, useEffect, useState } from 'react';
import {
    ResponsiveContainer, BarChart, Bar, LineChart, Line,
    XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

interface MonthRow {
    month: string;
    visits: number;
    unique: number;
    clicks: number;
    quiz: number;
    trials: number;
    payments: number;
    revenue: number;
}
interface Stats {
    code: string | null;
    currency?: string;
    months: MonthRow[];
    totals: { visits: number; unique: number; clicks: number; quiz: number; trials: number; payments: number; revenue: number };
}

const ink = '#0a0a0a';
const ivory = '#e8e6e0';
const muted = '#8a8678';
const accent = '#008558';

const card: React.CSSProperties = {
    background: '#0c0b0a', border: '1px solid rgba(232,230,224,0.12)', borderRadius: 8, padding: 20,
};
const btn: React.CSSProperties = {
    background: accent, color: ivory, border: 'none', padding: '12px 20px', borderRadius: 6,
    fontWeight: 600, cursor: 'pointer', fontSize: 14,
};

const ReferralsPage: React.FC = () => {
    const { user, session, loading: authLoading, signOut } = useAuth();

    // login state
    const [email, setEmail] = useState('');
    const [loginState, setLoginState] = useState<'idle' | 'sending' | 'sent' | 'not_approved' | 'error'>('idle');

    // dashboard state
    const [code, setCode] = useState<string | null>(null);
    const [link, setLink] = useState<string | null>(null);
    const [stats, setStats] = useState<Stats | null>(null);
    const [notApproved, setNotApproved] = useState(false);
    const [minting, setMinting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [desiredCode, setDesiredCode] = useState('');
    const [codeError, setCodeError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);

    const token = session?.access_token;

    const authedFetch = useCallback(
        (url: string, init: Parameters<typeof fetch>[1] = {}) =>
            fetch(url, {
                ...init,
                headers: { ...(init.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            }),
        [token],
    );

    // Load the partner's code + stats once authenticated.
    useEffect(() => {
        if (!token) return;
        let alive = true;
        (async () => {
            const codeRes = await authedFetch('/api/referral-code', { method: 'GET' });
            if (codeRes.status === 403) { if (alive) setNotApproved(true); return; }
            const codeJson = await codeRes.json().catch(() => ({}));
            if (!alive) return;
            setCode(codeJson.code || null);
            setLink(codeJson.link || null);

            const statsRes = await authedFetch('/api/referral-stats', { method: 'GET' });
            if (statsRes.status === 403) { if (alive) setNotApproved(true); return; }
            const statsJson = await statsRes.json().catch(() => null);
            if (alive && statsJson) setStats(statsJson);
        })();
        return () => { alive = false; };
    }, [token, authedFetch]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginState('sending');
        try {
            const res = await fetch('/api/referral-login', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.status === 403) setLoginState('not_approved');
            else if (res.ok) setLoginState('sent');
            else setLoginState('error');
        } catch {
            setLoginState('error');
        }
    };

    // Claim a custom (vanity) code, or pass nothing to auto-generate one.
    const claimCode = async (custom?: string) => {
        setMinting(true);
        setCodeError(null);
        const res = await authedFetch('/api/referral-code', {
            method: 'POST',
            body: JSON.stringify(custom ? { code: custom } : {}),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            setCode(json.code);
            setLink(json.link);
            setEditing(false);
            setDesiredCode('');
        } else {
            const map: Record<string, string> = {
                taken: 'That code is already taken - try another.',
                invalid_format: 'Use 3–32 chars: lowercase letters, digits and hyphens (no leading/trailing hyphen).',
                reserved: 'That word is reserved - pick another.',
            };
            setCodeError(map[json.error] || 'Could not save code. Try again.');
        }
        setMinting(false);
    };

    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    const copyLink = () => {
        if (!link) return;
        navigator.clipboard?.writeText(link).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const shell = (children: React.ReactNode) => (
        <div style={{ minHeight: '100vh', background: ink, color: ivory, fontFamily: "'Geist', -apple-system, sans-serif", padding: '48px 20px' }}>
            <div style={{ maxWidth: 920, margin: '0 auto' }}>{children}</div>
        </div>
    );

    if (authLoading) return shell(<p style={{ color: muted }}>Loading…</p>);

    // ── Not logged in: approval-gated magic-link login ──
    if (!user) {
        return shell(
            <div style={{ maxWidth: 420, margin: '6vh auto' }}>
                <h1 style={{ fontSize: 30, fontWeight: 300, marginBottom: 8 }}>Referral partners</h1>
                <p style={{ color: muted, marginBottom: 24, lineHeight: 1.6 }}>
                    Sign in with your approved email. We&apos;ll send a secure magic link.
                </p>
                <form onSubmit={handleLogin} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <input
                        type="email" required placeholder="you@example.com" value={email}
                        onChange={(e) => setEmail(e.target.value)} disabled={loginState === 'sending' || loginState === 'sent'}
                        style={{ background: 'rgba(232,230,224,0.04)', border: '1px solid rgba(232,230,224,0.15)', borderRadius: 6, padding: '12px 14px', color: ivory, fontSize: 14 }}
                    />
                    <button type="submit" style={btn} disabled={loginState === 'sending' || loginState === 'sent'}>
                        {(() => {
                            if (loginState === 'sending') return 'Sending…';
                            if (loginState === 'sent') return '✓ Check your email';
                            return 'Send magic link';
                        })()}
                    </button>
                    {loginState === 'not_approved' && (
                        <p style={{ color: '#ef4444', fontSize: 13 }}>This email isn&apos;t an approved referral partner yet.</p>
                    )}
                    {loginState === 'error' && (
                        <p style={{ color: '#ef4444', fontSize: 13 }}>Something went wrong. Try again.</p>
                    )}
                </form>
            </div>,
        );
    }

    // ── Logged in but not an approved referrer ──
    if (notApproved) {
        return shell(
            <div style={{ maxWidth: 460, margin: '6vh auto' }}>
                <h1 style={{ fontSize: 26, fontWeight: 300, marginBottom: 10 }}>Not a referral partner</h1>
                <p style={{ color: muted, lineHeight: 1.6 }}>
                    Your account ({user.email}) isn&apos;t approved as a referral partner. Contact us to join.
                </p>
                <button type="button" onClick={() => signOut()} style={{ ...btn, marginTop: 20, background: 'transparent', border: `1px solid ${muted}`, color: muted }}>
                    Sign out
                </button>
            </div>,
        );
    }

    // ── Approved referrer dashboard ──
    const totals = stats?.totals;
    const currency = stats?.currency || 'USD';

    return shell(
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24 }}>
                <h1 style={{ fontSize: 30, fontWeight: 300 }}>Your referrals</h1>
                <button type="button" onClick={() => signOut()} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
            </div>

            {/* Referral link */}
            <div style={{ ...card, marginBottom: 24 }}>
                {code && !editing ? (
                    <>
                        <div style={{ fontSize: 11, color: muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Your referral link</div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <code style={{ fontSize: 15, color: ivory, background: 'rgba(0,133,88,0.12)', padding: '8px 12px', borderRadius: 6 }}>{link}</code>
                            <button type="button" onClick={copyLink} style={{ ...btn, padding: '8px 16px' }}>{copied ? '✓ Copied' : 'Copy'}</button>
                            <button type="button" onClick={() => { setEditing(true); setDesiredCode(code); }} style={{ background: 'none', border: `1px solid ${muted}`, color: muted, padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Change</button>
                        </div>
                    </>
                ) : (
                    <div>
                        <div style={{ fontSize: 16, marginBottom: 4 }}>{code ? 'Change your referral link' : 'Create your referral link'}</div>
                        <div style={{ color: muted, fontSize: 13, marginBottom: 14 }}>Pick your own code - changing it stops the old link from tracking.</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ color: muted, fontSize: 14 }}>{origin}/r/</span>
                            <input
                                value={desiredCode}
                                onChange={(e) => setDesiredCode(e.target.value.toLowerCase())}
                                placeholder="your-code"
                                style={{ background: 'rgba(232,230,224,0.04)', border: '1px solid rgba(232,230,224,0.15)', borderRadius: 6, padding: '10px 12px', color: ivory, fontSize: 14, minWidth: 180 }}
                            />
                            <button type="button" onClick={() => claimCode(desiredCode.trim())} style={btn} disabled={minting || desiredCode.trim().length < 3}>
                                {(() => {
                                    if (minting) return 'Saving…';
                                    return code ? 'Save' : 'Claim';
                                })()}
                            </button>
                            <button type="button" onClick={() => claimCode()} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }} disabled={minting}>
                                or generate random
                            </button>
                            {code && (
                                <button type="button" onClick={() => { setEditing(false); setCodeError(null); setDesiredCode(''); }} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            )}
                        </div>
                        {codeError && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 10 }}>{codeError}</p>}
                    </div>
                )}
            </div>

            {/* Totals */}
            {totals && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 24 }}>
                    <Stat label="Visits" value={totals.visits} />
                    <Stat label="Unique visitors" value={totals.unique} />
                    <Stat label="Quiz" value={totals.quiz} />
                    <Stat label="Trials" value={totals.trials} />
                    <Stat label="Payments" value={totals.payments} />
                    <Stat label={`Revenue (${currency})`} value={totals.revenue.toFixed(2)} />
                </div>
            )}

            {/* Charts */}
            {stats && stats.months.length > 0 ? (
                <>
                    <div style={{ ...card, marginBottom: 20 }}>
                        <ChartTitle>Funnel by month</ChartTitle>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={stats.months}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(232,230,224,0.08)" />
                                <XAxis dataKey="month" stroke={muted} fontSize={12} />
                                <YAxis stroke={muted} fontSize={12} allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(232,230,224,0.06)' }}
                                    contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 6 }}
                                />
                                <Legend />
                                <Bar dataKey="visits" fill="#6b7280" />
                                <Bar dataKey="unique" fill="#a78bfa" />
                                <Bar dataKey="quiz" fill="#3b82f6" />
                                <Bar dataKey="trials" fill="#eab308" />
                                <Bar dataKey="payments" fill={accent} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div style={card}>
                        <ChartTitle>Revenue by month ({currency})</ChartTitle>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={stats.months}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(232,230,224,0.08)" />
                                <XAxis dataKey="month" stroke={muted} fontSize={12} />
                                <YAxis stroke={muted} fontSize={12} />
                                <Tooltip
                                    cursor={{ stroke: 'rgba(232,230,224,0.15)' }}
                                    contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 6 }}
                                />
                                <Line type="monotone" dataKey="revenue" stroke={accent} strokeWidth={2} dot />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </>
            ) : (
                code && <p style={{ color: muted }}>No activity yet. Share your link to see stats here.</p>
            )}
        </>,
    );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div style={card}>
        <div style={{ fontSize: 11, color: muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 300 }}>{value}</div>
    </div>
);

const ChartTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ fontSize: 13, color: muted, marginBottom: 14, letterSpacing: '0.05em' }}>{children}</div>
);

export default ReferralsPage;
